/**
 * Replace backend mention markup `@[Display Name](uuid)` with a styled
 * inline <span> pill.
 *
 * Operates on already-sanitized HTML (DOMPurify output). The regex match
 * runs against **text nodes only** via a TreeWalker — never against
 * attribute values — so a sanitized anchor like
 * `<a href="@[X](abc-123)">click</a>` cannot have its href clobbered by
 * the replacement. (Mantas's review on PR #965: the prior string-level
 * regex replace would splice `<span class="becreatives-mention" …>` into
 * the href attribute value, producing broken HTML and accidentally
 * injecting a `data-user-id` attribute onto the anchor.)
 *
 * The regex matches the backend parser
 * (RequestRevisionComment::parseMentionsFromText) — anything rendered as a
 * pill is the same set of tokens the backend extracted as notification
 * targets when the comment was created.
 */

const MENTION_REGEX = /@\[([^\]]+)\]\(([a-fA-F0-9-]+)\)/gi;

export const injectMentionPillsInHtml = (sanitizedHtml: string | null | undefined): string => {
  if (!sanitizedHtml) {
    return '';
  }

  // `<template>` parses the HTML in a document fragment context and gives
  // us back a real DocumentFragment we can walk safely. Using `innerHTML`
  // here is safe because the input is already DOMPurify-sanitized.
  const template = document.createElement('template');
  template.innerHTML = sanitizedHtml;

  replaceMentionsInTextNodes(template.content);

  return template.innerHTML;
};

const replaceMentionsInTextNodes = (root: Node): void => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodesWithMentions: Text[] = [];

  // Collect first, mutate after — replacing a text node mid-walk would
  // invalidate the walker's cursor.
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const text = node.nodeValue;
    if (text && text.includes('@[') && hasMentionMarkup(text)) {
      textNodesWithMentions.push(node as Text);
    }
  }

  for (const textNode of textNodesWithMentions) {
    const fragment = buildMentionFragment(textNode.nodeValue || '');
    textNode.parentNode?.replaceChild(fragment, textNode);
  }
};

const hasMentionMarkup = (text: string): boolean => {
  // Fresh regex instance because MENTION_REGEX is `g`-flagged module state —
  // `.test()` on the shared instance would leak `lastIndex` between calls.
  return /@\[([^\]]+)\]\(([a-fA-F0-9-]+)\)/i.test(text);
};

const buildMentionFragment = (text: string): DocumentFragment => {
  const fragment = document.createDocumentFragment();
  const re = /@\[([^\]]+)\]\(([a-fA-F0-9-]+)\)/gi;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    const [fullMatch, display, userId] = match;

    if (match.index > cursor) {
      fragment.appendChild(document.createTextNode(text.slice(cursor, match.index)));
    }

    // setAttribute + textContent let the DOM API handle attribute/text
    // escaping — no manual escapeAttr / escapeText helpers needed.
    const span = document.createElement('span');
    span.className = 'becreatives-mention';
    span.setAttribute('data-user-id', userId);
    span.textContent = `@${display}`;
    fragment.appendChild(span);

    cursor = match.index + fullMatch.length;
  }

  if (cursor < text.length) {
    fragment.appendChild(document.createTextNode(text.slice(cursor)));
  }

  return fragment;
};

/**
 * Strip mention markup down to "@Display Name" for plain-text contexts
 * (clipboard, notifications, search hit previews). Leaves everything else
 * untouched. Plain-text only — no HTML context to corrupt.
 */
export const stripMentionsToPlainText = (rawText: string | null | undefined): string => {
  if (!rawText) {
    return '';
  }

  return rawText.replace(MENTION_REGEX, (_match, display: string) => `@${display}`);
};

/**
 * Translate a cursor position from the textarea's displayed plain text back
 * to the corresponding index in the underlying markup string.
 *
 * react-mentions stores `@[Display Name](uuid)` (markup) but renders
 * `@Display Name` (plain text) in the textarea. `textareaRef.selectionStart`
 * is in plain-text coordinates; without this mapping, slicing the markup at
 * that offset lands inside a mention token and any inserted text shreds it
 * — see Marius and Mantas review on PRs #184 / #965.
 *
 * Caret inside a mention's displayed text snaps to the END of the mention's
 * markup (post-`)`). Inserting BEFORE a mention is rare and ambiguous; end-
 * snap keeps the existing pill intact, which is the safer default.
 */
export const mapPlainCaretToMarkup = (markup: string, plainCaret: number): number => {
  if (!markup || plainCaret <= 0) {
    return Math.max(0, plainCaret);
  }

  let plainPos = 0;
  let markupPos = 0;
  // Use a fresh regex instance — MENTION_REGEX is shared module state with
  // the `g` flag, so reusing it concurrently would leave lastIndex stale.
  const re = /@\[([^\]]+)\]\(([a-fA-F0-9-]+)\)/gi;
  let match: RegExpExecArray | null;

  while ((match = re.exec(markup)) !== null) {
    const [fullMatch, display] = match;
    const nonMentionLen = match.index - markupPos;

    // Caret is somewhere in the non-mention run before this mention.
    if (plainCaret <= plainPos + nonMentionLen) {
      return markupPos + (plainCaret - plainPos);
    }
    plainPos += nonMentionLen;
    markupPos += nonMentionLen;

    // Caret falls inside the mention's displayed text. The displayTransform
    // is `@${display}` so plain length is 1 + display.length.
    const plainMentionLen = 1 + display.length;
    if (plainCaret < plainPos + plainMentionLen) {
      return markupPos + fullMatch.length;
    }
    plainPos += plainMentionLen;
    markupPos += fullMatch.length;
  }

  // Trailing non-mention run after the last mention.
  return markupPos + (plainCaret - plainPos);
};
